import type { ReactNode } from 'react';
import clsx from 'clsx';
import type { PublicoCiencia, StatusPublicacao } from '../publicacao.types';
import { AVISO_AGENDAMENTO, dataHoraAgendamento } from '../publicacao.definitions';
import type { FormPublicacao } from './publicacaoEditor';
import { SeletorPessoas, SeletorProjetos } from './SeletoresPublico';

interface OpcaoChipProps {
  nome: string;
  marcada: boolean;
  onSelect: () => void;
  children: ReactNode;
}

function OpcaoChip({ nome, marcada, onSelect, children }: OpcaoChipProps) {
  return (
    <label
      className={clsx(
        'flex cursor-pointer items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs transition-colors',
        marcada ? 'border-primary-300 bg-primary-50 font-semibold text-primary-800' : 'border-raro-gray-200 text-raro-gray-700'
      )}
    >
      <input type="radio" name={nome} checked={marcada} onChange={onSelect} className="text-primary-600" />
      {children}
    </label>
  );
}

const BLOCO = 'space-y-3 rounded-xl border border-raro-gray-200 p-4';
const LEGENDA = 'px-1 text-sm font-medium text-raro-gray-700';
const PERGUNTA = 'text-xs font-medium text-raro-gray-600';

interface EditorCienciaDestaqueProps {
  form: FormPublicacao;
  onChange: (parcial: Partial<FormPublicacao>) => void;
  status: StatusPublicacao;
  /** Documento não é fixado (Destaque só nos posts). */
  mostrarDestaque?: boolean;
}

/** Ciência (de quem + prazo opcional), Destaque (fixar, com ou sem data limite) e Quando publicar. */
export function EditorCienciaDestaque({ form, onChange, status, mostrarDestaque = true }: EditorCienciaDestaqueProps) {
  const jaPublicada = status === 'publicada' || status === 'despublicada';
  const publicos: Array<{ valor: PublicoCiencia; label: string }> = [
    { valor: 'todos', label: 'Todos os ativos' },
    { valor: 'projetos', label: 'Por projeto' },
    { valor: 'pessoas', label: 'Pessoas escolhidas' },
  ];

  return (
    <div className="space-y-4">
      <fieldset className={BLOCO}>
        <legend className={LEGENDA}>Ciência</legend>
        {form.cienciaPeloDocumento ? (
          <p className="text-sm text-raro-gray-600">
            A ciência é a do <b>documento relacionado</b> — quem abrir o post vê &quot;Ver documento e confirmar&quot;. Para o
            post ter ciência própria, tire o documento relacionado.
          </p>
        ) : (
        <>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            className="h-4 w-4 rounded text-primary-600"
            checked={form.exigeCiencia}
            onChange={(event) => onChange({ exigeCiencia: event.target.checked })}
          />
          Pedir &quot;Li e estou ciente&quot;
        </label>
        {form.exigeCiencia && (
          <div className="space-y-3 pl-6">
            <div>
              <p className={PERGUNTA}>De quem?</p>
              <div className="mt-1 flex flex-wrap gap-2">
                {publicos.map((p) => (
                  <OpcaoChip key={p.valor} nome="publico" marcada={form.publico === p.valor} onSelect={() => onChange({ publico: p.valor })}>
                    {p.label}
                  </OpcaoChip>
                ))}
              </div>
              {form.publico === 'projetos' && (
                <SeletorProjetos selecionados={form.projetoIds} onChange={(projetoIds) => onChange({ projetoIds })} />
              )}
              {form.publico === 'pessoas' && (
                <SeletorPessoas selecionados={form.pessoaIds} onChange={(pessoaIds) => onChange({ pessoaIds })} />
              )}
              <p className="mt-1 text-xs text-raro-gray-500">
                A publicação continua visível para todos. Isso só define de quem a ciência é cobrada e quem entra no relatório.
                {form.publico === 'projetos' && ' Vale quem estiver alocado nesses projetos quando você salvar.'}
              </p>
            </div>
            <div>
              <p className={PERGUNTA}>Tem prazo para confirmar?</p>
              <div className="mt-1 flex flex-wrap items-center gap-2">
                <OpcaoChip nome="prazo" marcada={!form.comPrazo} onSelect={() => onChange({ comPrazo: false })}>
                  Sem prazo
                </OpcaoChip>
                <OpcaoChip nome="prazo" marcada={form.comPrazo} onSelect={() => onChange({ comPrazo: true })}>
                  Com prazo
                </OpcaoChip>
                {form.comPrazo && (
                  <input
                    type="date"
                    className="rounded-lg border border-raro-gray-300 px-2 py-1 text-xs"
                    value={form.prazo}
                    onChange={(event) => onChange({ prazo: event.target.value })}
                  />
                )}
              </div>
            </div>
          </div>
        )}
        </>
        )}
      </fieldset>

      {mostrarDestaque && (
      <fieldset className={BLOCO}>
        <legend className={LEGENDA}>Destaque</legend>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            className="h-4 w-4 rounded text-primary-600"
            checked={form.fixar}
            onChange={(event) => onChange({ fixar: event.target.checked })}
          />
          📌 Fixar no topo da Início
        </label>
        {form.fixar && (
          <div className="pl-6">
            <p className={PERGUNTA}>Tem data limite?</p>
            <div className="mt-1 flex flex-wrap items-center gap-2">
              <OpcaoChip nome="fixar-ate" marcada={!form.fixarComLimite} onSelect={() => onChange({ fixarComLimite: false })}>
                Sem data limite
              </OpcaoChip>
              <OpcaoChip nome="fixar-ate" marcada={form.fixarComLimite} onSelect={() => onChange({ fixarComLimite: true })}>
                Com data limite
              </OpcaoChip>
              {form.fixarComLimite && (
                <input
                  type="date"
                  className="rounded-lg border border-raro-gray-300 px-2 py-1 text-xs"
                  value={form.fixarAte}
                  onChange={(event) => onChange({ fixarAte: event.target.value })}
                />
              )}
            </div>
          </div>
        )}
        <p className="text-xs text-raro-gray-500">Fixadas aparecem primeiro no feed. No máximo 3 ao mesmo tempo.</p>
      </fieldset>
      )}

      {!jaPublicada && (
        <fieldset className={BLOCO}>
          <legend className={LEGENDA}>Quando publicar</legend>
          <div className="flex flex-wrap items-center gap-2">
            <OpcaoChip nome="quando" marcada={!form.agendar} onSelect={() => onChange({ agendar: false })}>
              Agora
            </OpcaoChip>
            <OpcaoChip nome="quando" marcada={form.agendar} onSelect={() => onChange({ agendar: true })}>
              Agendar
            </OpcaoChip>
            {form.agendar && (
              <input
                type="datetime-local"
                className="rounded-lg border border-raro-gray-300 px-2 py-1 text-xs"
                value={form.publicarEm}
                onChange={(event) => onChange({ publicarEm: event.target.value })}
              />
            )}
          </div>
          {form.agendar && (
            <div className="space-y-1 text-xs">
              {form.publicarEm && (
                <p className="font-medium text-raro-gray-700">
                  Será publicada em {dataHoraAgendamento(new Date(form.publicarEm))}.
                </p>
              )}
              <p className="text-raro-gray-500">{AVISO_AGENDAMENTO}</p>
            </div>
          )}
        </fieldset>
      )}
    </div>
  );
}
